/**
 * Search client — picks the Web Worker when available, falls back to a
 * main-thread engine otherwise (jsdom tests, locked-down browsers).
 *
 * The fallback is also the graceful-degradation path: search must work even
 * when a module Worker can't be spawned.
 */
import { SearchEngine, type SearchRecord, type SearchHit } from './searchRecords.js';
import type { WorkerInbound, WorkerOutbound } from './searchWorker.js';

export interface SearchClient {
  /** (Re)build the index. Resolves with the record count. */
  index: (records: SearchRecord[]) => Promise<number>;
  /** Query the current index. */
  query: (q: string) => Promise<SearchHit[]>;
  /** Tear down any worker. */
  dispose: () => void;
}

/** Main-thread client — builds {@link SearchEngine} synchronously. */
export function createMainThreadSearchClient(): SearchClient {
  let engine: SearchEngine | null = null;
  return {
    index(records) {
      engine = new SearchEngine(records);
      return Promise.resolve(engine.size);
    },
    query(q) {
      return Promise.resolve(engine ? engine.search(q) : []);
    },
    dispose() {
      engine = null;
    },
  };
}

/* c8 ignore start — Worker-backed client; covered by the main-thread path + manual. */

/** Worker-backed client — builds the index off the main thread (PERF-04). */
export function createWorkerSearchClient(worker: Worker): SearchClient {
  let queryId = 0;
  const pending = new Map<number, (hits: SearchHit[]) => void>();
  let onReady: ((size: number) => void) | null = null;

  worker.onmessage = (event: MessageEvent<WorkerOutbound>): void => {
    const msg = event.data;
    if (msg.type === 'ready') {
      onReady?.(msg.size);
      onReady = null;
    } else if (msg.type === 'results') {
      pending.get(msg.id)?.(msg.hits);
      pending.delete(msg.id);
    }
  };

  return {
    index(records) {
      return new Promise<number>((resolve) => {
        onReady = resolve;
        worker.postMessage({ type: 'index', records } satisfies WorkerInbound);
      });
    },
    query(q) {
      return new Promise<SearchHit[]>((resolve) => {
        const id = ++queryId;
        pending.set(id, resolve);
        worker.postMessage({ type: 'query', id, query: q } satisfies WorkerInbound);
      });
    },
    dispose() {
      worker.terminate();
      pending.clear();
    },
  };
}

/**
 * Create the best available search client. Tries to spawn the module Worker;
 * falls back to the main-thread engine when Workers are unavailable.
 */
export function createSearchClient(): SearchClient {
  if (typeof Worker === 'undefined') {
    return createMainThreadSearchClient();
  }
  try {
    const worker = new Worker(new URL('./searchWorker.js', import.meta.url), {
      type: 'module',
    });
    return createWorkerSearchClient(worker);
  } catch {
    return createMainThreadSearchClient();
  }
}

/* c8 ignore stop */
